import { Link } from "react-router-dom";
import { ArrowLeft, Feather } from "lucide-react";
import Footer from "../components/Footer";

export default function About() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm border-b border-[#E6E2DD]/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2D2A26] hover:text-[#A87C6D] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <Link 
            to="/"
            className="text-xl font-light text-[#2D2A26] tracking-wide"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            the soft ritual
          </Link>
          <div className="w-16"></div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pt-24 pb-16 px-6">
        <article className="max-w-2xl mx-auto">
          
          {/* Hero Section */}
          <section className="text-center mb-12">
            <div className="w-12 h-12 rounded-full bg-[#A87C6D]/10 flex items-center justify-center mx-auto mb-6">
              <Feather className="w-6 h-6 text-[#A87C6D]" />
            </div>
            <h1 
              className="text-3xl md:text-4xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              About The Soft Ritual
            </h1>
            <p className="text-base text-[#A87C6D] font-light">
              A quiet space to pause.
            </p>
          </section>

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-6">
              The Soft Ritual is designed for moments when you're carrying a lot, feeling unsure, 
              or simply need somewhere gentle to land—without being fixed, optimized, or analyzed.
            </p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-2">This isn't therapy.</p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-2">It isn't journaling.</p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-6">And it isn't about becoming a better version of yourself.</p>
            <p className="text-base text-[#2D2A26] font-normal leading-relaxed">
              The Soft Ritual exists to support slower moments, clearer choices, and steadier self-trust.
            </p>
          </section>

          {/* What We Mean by Ritual */}
          <section className="mb-12">
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What We Mean by Ritual
            </h2>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-4">
              In The Soft Ritual, a ritual is a small, intentional act that helps you mark a moment.
            </p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-6">
              It might be a breath, a pause, a boundary, a sentence you finally allow yourself to say, 
              or a moment of stillness before moving on.
            </p>
            <div className="bg-[#F5F2ED] rounded-2xl p-6">
              <p className="text-base text-[#4A4744] font-light mb-2">Rituals here are practical, optional, and brief.</p>
              <p className="text-base text-[#4A4744] font-light mb-2">No belief system required.</p>
              <p className="text-base text-[#4A4744] font-light mb-4">No special tools needed.</p>
              <p className="text-base text-[#2D2A26] font-normal">
                They're simply a way to respond—rather than react.
              </p>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-12">
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              How The Soft Ritual Works
            </h2>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-4">
              Each time you open the app, you're invited to check in gently.
            </p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-4">
              The Soft Ritual App listens for what's underneath your words and responds in three simple ways:
            </p>
            <ul className="space-y-2 mb-6 ml-4">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">by reflecting what it hears,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">by supporting steadiness when things feel unsettled,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">and sometimes by offering a small ritual—only when it feels appropriate.</span>
              </li>
            </ul>
            <p className="text-base text-[#2D2A26] font-light italic">
              Often, no ritual is needed at all. Stillness counts here.
            </p>
          </section>

          {/* What Makes It Different */}
          <section className="mb-12">
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              What Makes The Soft Ritual Different
            </h2>
            <p className="text-base text-[#A87C6D] font-normal mb-4">
              The Soft Ritual is intentionally restrained.
            </p>
            <p className="text-base text-[#4A4744] font-light mb-2">There are no streaks to maintain.</p>
            <p className="text-base text-[#4A4744] font-light mb-2">No dashboards to review.</p>
            <p className="text-base text-[#4A4744] font-light mb-6">No pressure to show up daily or do more.</p>
            <div className="border-l-2 border-[#A87C6D]/30 pl-6 mb-6">
              <p className="text-base text-[#4A4744] font-light leading-relaxed mb-3">
                You won't be rushed toward insight or pushed toward positivity.
              </p>
              <p className="text-base text-[#4A4744] font-light leading-relaxed mb-3">
                Joy is welcomed—but never forced.
              </p>
              <p className="text-base text-[#4A4744] font-light leading-relaxed">
                Hard moments are met without being turned into lessons.
              </p>
            </div>
            <p className="text-base text-[#2D2A26] font-normal">
              This is a space where nothing is required of you.
            </p>
          </section>

          {/* Who It's For */}
          <section className="mb-12">
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Who The Soft Ritual Is For
            </h2>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-4">
              The Soft Ritual is for people who:
            </p>
            <ul className="space-y-2 mb-6 ml-4">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">are emotionally aware but often tired,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">hold space for others and need somewhere to land themselves,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">want clarity without urgency,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A87C6D] mt-2 flex-shrink-0"></span>
                <span className="text-base text-[#4A4744] font-light">value softness, honesty, and self-trust.</span>
              </li>
            </ul>
            <div className="bg-[#A87C6D]/5 rounded-2xl p-6 border border-[#A87C6D]/10">
              <p className="text-base text-[#2D2A26] font-light italic leading-relaxed">
                If you've ever thought "I don't need advice—I just need a moment,"
                The Soft Ritual was made for you.
              </p>
            </div>
          </section>

          {/* Final Note */}
          <section className="mb-8">
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              A Final Note
            </h2>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-2">The more you return, the more The Soft Ritual learns to meet you.</p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-2">Each check-in is a small act of self-care—a moment to pause and reconnect.</p>
            <p className="text-base text-[#4A4744] font-light leading-relaxed mb-6">Over time, these moments become a practice, and the practice becomes a rhythm you can trust.</p>
            <p className="text-base text-[#2D2A26] font-normal leading-relaxed mb-8">
              You deserve a space that's always here for you—ready when you are.
            </p>
            <div className="text-center py-6 border-t border-[#E6E2DD]">
              <p 
                className="text-lg text-[#A87C6D] font-light italic"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                The Soft Ritual is here—whenever you need a moment to breathe.
              </p>
            </div>
          </section>

        </article>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
